import React, { useState } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Steps,
  Table,
  Form,
  Select,
  Switch,
  Input,
  Alert,
  Progress,
  Space,
  Divider,
  Row,
  Col,
  message,
} from 'antd';
import {
  UploadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { IMPORT_FORMATS, DATA_TYPES, FILE_SIZE_LIMITS } from '../constants';
import type {
  ImportConfig,
  FieldMapping,
  ImportOptions,
  ValidationRules,
  FileUpload,
  ImportStatus,
} from '../types';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Step } = Steps;
const { Option } = Select;

const ImportData: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [importConfig, setImportConfig] = useState<Partial<ImportConfig>>({
    dataType: 'users',
    format: 'csv',
    status: 'pending',
  });
  const [fileUpload, setFileUpload] = useState<FileUpload | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [sampleData, setSampleData] = useState<any[]>([]);
  const [form] = Form.useForm();

  // Mock available target fields for different data types
  const getTargetFields = (dataType: string) => {
    const fieldMap: Record<string, string[]> = {
      users: ['firstName', 'lastName', 'email', 'role', 'department', 'status'],
      clients: ['name', 'email', 'phone', 'address', 'dateOfBirth', 'gender'],
      coaches: ['name', 'email', 'specialization', 'certification', 'experience'],
      programs: ['name', 'description', 'duration', 'difficulty', 'category'],
    };
    return fieldMap[dataType] || [];
  };

  // File upload configuration
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    beforeUpload: (file) => {
      const isValidFormat = IMPORT_FORMATS.some(format =>
        format.accepts.split(',').some(ext => file.name.toLowerCase().endsWith(ext.trim()))
      );
      
      if (!isValidFormat) {
        message.error('Invalid file format. Please upload CSV, Excel, or JSON files.');
        return false;
      }

      const maxSize = FILE_SIZE_LIMITS[importConfig.format as keyof typeof FILE_SIZE_LIMITS];
      if (file.size > maxSize) {
        message.error(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
        return false;
      }

      // Simulate file upload
      const upload: FileUpload = {
        file,
        uploadId: `upload_${Date.now()}`,
        progress: 0,
        status: 'uploading',
      };

      setFileUpload(upload);

      // Simulate upload progress
      const interval = setInterval(() => {
        setFileUpload(prev => {
          if (!prev || prev.progress >= 100) {
            clearInterval(interval);
            return prev;
          }
          const newProgress = prev.progress + 10;
          if (newProgress >= 100) {
            // Simulate parsing and showing sample data
            setSampleData([
              { firstName: 'John', lastName: 'Doe', email: 'john@example.com', role: 'Client' },
              { firstName: 'Jane', lastName: 'Smith', email: 'jane@example.com', role: 'Coach' },
              { firstName: 'Bob', lastName: 'Johnson', email: 'bob@example.com', role: 'Admin' },
            ]);
            return { ...prev, progress: 100, status: 'completed' };
          }
          return { ...prev, progress: newProgress };
        });
      }, 200);

      return false; // Prevent default upload
    },
  };

  // Generate field mappings from sample data
  const generateMappings = () => {
    if (sampleData.length === 0) return;

    const sourceFields = Object.keys(sampleData[0]);
    const targetFields = getTargetFields(importConfig.dataType!);
    
    const mappings: FieldMapping[] = sourceFields.map(sourceField => ({
      sourceField,
      targetField: targetFields.find(target => 
        target.toLowerCase() === sourceField.toLowerCase()
      ) || '',
      dataType: 'string',
      required: false,
    }));

    setFieldMappings(mappings);
  };

  // Step content components
  const renderFileUpload = () => (
    <Card>
      <Row gutter={24}>
        <Col span={12}>
          <Form layout="vertical">
            <Form.Item label="Data Type" required>
              <Select
                value={importConfig.dataType}
                onChange={(value) => setImportConfig(prev => ({ ...prev, dataType: value }))}
              >
                {DATA_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item label="File Format" required>
              <Select
                value={importConfig.format}
                onChange={(value) => setImportConfig(prev => ({ ...prev, format: value }))}
              >
                {IMPORT_FORMATS.map(format => (
                  <Option key={format.value} value={format.value}>
                    {format.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Col>
        
        <Col span={12}>
          <Dragger {...uploadProps} style={{ height: 200 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for CSV, Excel, and JSON files up to {Math.round(FILE_SIZE_LIMITS.csv / 1024 / 1024)}MB
            </p>
          </Dragger>
          
          {fileUpload && (
            <div style={{ marginTop: 16 }}>
              <Text strong>{fileUpload.file.name}</Text>
              <Progress
                percent={fileUpload.progress}
                status={fileUpload.status === 'completed' ? 'success' : 'active'}
                showInfo={true}
              />
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );

  const renderFieldMapping = () => (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>Field Mapping</Title>
        <Text type="secondary">
          Map the fields from your file to the system fields. Required fields must be mapped.
        </Text>
      </div>
      
      {sampleData.length > 0 && (
        <>
          <Alert
            message="Sample Data Preview"
            description="Here's a preview of your data. Use this to verify field mappings."
            type="info"
            style={{ marginBottom: 16 }}
          />
          
          <Table
            dataSource={sampleData.slice(0, 3)}
            pagination={false}
            size="small"
            style={{ marginBottom: 24 }}
          />
        </>
      )}

      <Button
        type="primary"
        onClick={generateMappings}
        style={{ marginBottom: 16 }}
        disabled={sampleData.length === 0}
      >
        Auto-Generate Mappings
      </Button>

      <Table
        dataSource={fieldMappings}
        pagination={false}
        rowKey="sourceField"
        columns={[
          {
            title: 'Source Field',
            dataIndex: 'sourceField',
            key: 'sourceField',
          },
          {
            title: 'Target Field',
            key: 'targetField',
            render: (_, record, index) => (
              <Select
                value={record.targetField}
                onChange={(value) => {
                  const newMappings = [...fieldMappings];
                  newMappings[index].targetField = value;
                  setFieldMappings(newMappings);
                }}
                style={{ width: '100%' }}
                allowClear
              >
                {getTargetFields(importConfig.dataType!).map(field => (
                  <Option key={field} value={field}>
                    {field}
                  </Option>
                ))}
              </Select>
            ),
          },
          {
            title: 'Data Type',
            key: 'dataType',
            render: (_, record, index) => (
              <Select
                value={record.dataType}
                onChange={(value) => {
                  const newMappings = [...fieldMappings];
                  newMappings[index].dataType = value;
                  setFieldMappings(newMappings);
                }}
                style={{ width: '100%' }}
              >
                <Option value="string">Text</Option>
                <Option value="number">Number</Option>
                <Option value="date">Date</Option>
                <Option value="email">Email</Option>
                <Option value="boolean">Boolean</Option>
              </Select>
            ),
          },
          {
            title: 'Required',
            key: 'required',
            render: (_, record, index) => (
              <Switch
                checked={record.required}
                onChange={(checked) => {
                  const newMappings = [...fieldMappings];
                  newMappings[index].required = checked;
                  setFieldMappings(newMappings);
                }}
              />
            ),
          },
        ]}
      />
    </Card>
  );

  const renderValidationSettings = () => (
    <Card>
      <Title level={4}>Import Settings & Validation</Title>
      
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Allow Duplicates"
              name="allowDuplicates"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>
            
            <Form.Item
              label="Skip Invalid Rows"
              name="skipInvalidRows"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
            
            <Form.Item
              label="Batch Size"
              name="batchSize"
              initialValue={100}
            >
              <Input type="number" min={1} max={1000} />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              label="Maximum Errors"
              name="maxErrors"
              initialValue={50}
            >
              <Input type="number" min={0} max={1000} />
            </Form.Item>
            
            <Form.Item
              label="Continue on Error"
              name="continueOnError"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
            
            <Form.Item
              label="Start Row"
              name="startRow"
              initialValue={1}
            >
              <Input type="number" min={1} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  const renderSummary = () => (
    <Card>
      <Title level={4}>Import Summary</Title>
      
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <Text strong>Data Type: </Text>
          <Text>{DATA_TYPES.find(t => t.value === importConfig.dataType)?.label}</Text>
        </div>
        
        <div>
          <Text strong>File: </Text>
          <Text>{fileUpload?.file.name}</Text>
        </div>
        
        <div>
          <Text strong>Format: </Text>
          <Text>{IMPORT_FORMATS.find(f => f.value === importConfig.format)?.label}</Text>
        </div>
        
        <div>
          <Text strong>Mapped Fields: </Text>
          <Text>{fieldMappings.filter(m => m.targetField).length} of {fieldMappings.length}</Text>
        </div>
        
        <Divider />
        
        <Alert
          message="Ready to Import"
          description="All settings have been configured. Click 'Start Import' to begin processing your data."
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
        />
        
        <Button
          type="primary"
          size="large"
          block
          onClick={() => {
            message.success('Import started successfully!');
            // Here you would typically start the actual import process
          }}
        >
          Start Import
        </Button>
      </Space>
    </Card>
  );

  const steps = [
    {
      title: 'Upload File',
      content: renderFileUpload(),
      icon: <UploadOutlined />,
    },
    {
      title: 'Field Mapping',
      content: renderFieldMapping(),
      icon: <ExclamationCircleOutlined />,
    },
    {
      title: 'Validation Settings',
      content: renderValidationSettings(),
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Review & Import',
      content: renderSummary(),
      icon: <LoadingOutlined />,
    },
  ];

  const next = () => {
    if (currentStep === 0 && (!fileUpload || fileUpload.status !== 'completed')) {
      message.error('Please upload a file first');
      return;
    }
    if (currentStep === 1 && fieldMappings.filter(m => m.targetField).length === 0) {
      message.error('Please map at least one field');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <div>
      <Card>
        <Title level={3}>Import Data</Title>
        <Text type="secondary">
          Upload and import data from external sources like CSV, Excel, or JSON files.
        </Text>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Steps current={currentStep} items={steps} />
        
        <div style={{ marginTop: 24 }}>
          {steps[currentStep].content}
        </div>
        
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            {currentStep > 0 && (
              <Button onClick={prev}>
                Previous
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={next}>
                Next
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default ImportData; 